import { CONSTANT_TEXTS } from '@gp/constants/const';
import SvgIcon from '@home/components/SvgIcon';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Bubble, BubbleProps } from 'tea-component';

import type { IBaseProps } from '@home/types/common';

import './index.scss';

interface ICalendarLegend {
  dotIcon: string;
  dotIconClassName: string;
  dotColor: string;
  text: string;
  tips?: string;
  tipsPlacement?: BubbleProps['placement'];
}

interface ILegendSignProps extends IBaseProps {
  loading?: boolean;
  segmentValue?: ScheduleSegmentTypes;
  options?: ICalendarLegend[];
  value?: boolean[];
  onChange?: (v: boolean[]) => void;
}

const DEFAULT_OPT: ICalendarLegend[] = [
  {
    dotIcon: '',
    dotIconClassName: '',
    dotColor: '#DEF2E4',
    text: 'Gate Review',
    tips: '',
    tipsPlacement: 'top',
  },
  {
    dotIcon: '',
    dotIconClassName: '',
    dotColor: '#CFE4FF',
    text: 'Milestone',
    tips: '',
    tipsPlacement: 'top',
  },
  {
    dotIcon: '',
    dotIconClassName: '',
    dotColor: '#EBECF1',
    text: CONSTANT_TEXTS.COMPLETED,
    tips: '',
    tipsPlacement: 'top',
  },
  {
    dotIcon: 'updating',
    dotIconClassName: 'dbg-sign-change',
    dotColor: '',
    text: 'Time Change',
    tips: 'Milestone updated in the past 30 days',
    tipsPlacement: 'top-end',
  },
];

const renderDotElement = (legend?: ICalendarLegend) => {
  if (!legend) return null;
  const { dotIcon, dotIconClassName, dotColor } = legend || {};
  if (dotIcon) return <SvgIcon type={dotIcon} className={dotIconClassName} />;
  return <i style={{ backgroundColor: dotColor }} className="dbg-sign-icon"></i>;
};

const getDefaultSelect = (options?: any[]): boolean[] => {
  if (!Array.isArray(options) || !options.length) return [];
  return options.map((option) => option?.text !== 'Time Change');
};

const getInitialValue = (value?: boolean[], options?: ICalendarLegend[]) => {
  if (Array.isArray(value) && !!value.length) return value;
  return getDefaultSelect(options);
};

const LegendSign: React.FC<ILegendSignProps> = (props) => {
  const {
    style,
    className,
    loading = false,
    segmentValue = '',
    options = DEFAULT_OPT,
    value: valueProps,
    onChange,
  } = props;

  const [value, setValue] = useState<boolean[]>(() => getInitialValue(valueProps, options));

  useEffect(() => {
    if (Array.isArray(valueProps) && !!valueProps.length) {
      setValue(valueProps);
    }
  }, [valueProps]);

  return (
    <div style={style} className={classnames('dbg-schedule-calendar', className)}>
      {Array.isArray(options) &&
        options.map((t, i) => {
          const { text, tipsPlacement, tips } = t || {};
          if (segmentValue === '1' && text === 'Milestone') return null;
          return (
            <Bubble key={i} dark arrowPointAtCenter placement={tipsPlacement} content={tips}>
              <span
                style={{ opacity: value?.[i] ? '1' : '0.45' }}
                className={classnames('dbg-sign-item', { 'sign-item-disabled': loading })}
                onClick={() => {
                  if (loading) return;
                  const valueTmp =
                    value?.map((item, itemIdx) => (i === itemIdx ? !item : item)) || [];
                  onChange?.(valueTmp);
                  setValue(valueTmp);
                }}
              >
                {renderDotElement(t)}
                <span className="dbg-legend-text">{text || '-'}</span>
              </span>
            </Bubble>
          );
        })}
    </div>
  );
};

export default React.memo(LegendSign);
